/**
 * Optimized Image Component
 * 
 * Features:
 * - Lazy loading with Intersection Observer
 * - Progressive loading (blur placeholder)
 * - Error handling with fallback
 * - WebP support detection
 * - Responsive images (srcset)
 */

import React, { useState, useEffect, useRef, memo } from 'react';
import { AVATAR_PALETTE, SURFACE } from '../styles/colors';
import { getImageVariants } from '../utils/imageVariants';
import { resolveImageSrc } from '../utils/imageUrl';
import '../styles/OptimizedImage.css';

// Check WebP support
let webpSupported = null;

/**
 * Check if WebP format is supported by the browser
 */
export function supportsWebP() {
  if (webpSupported !== null) return webpSupported;
  
  const canvas = document.createElement('canvas');
  webpSupported = canvas.toDataURL('image/webp').startsWith('data:image/webp');
  return webpSupported;
}

/**
 * Generate blur placeholder (exported for potential future use)
 * @param {number} width - Placeholder width
 * @param {number} height - Placeholder height
 * @returns {string} Data URL for placeholder SVG
 */
// eslint-disable-next-line no-unused-vars
function generatePlaceholder(width, height) {
  return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${width} ${height}'%3E%3Crect fill='%23f0f0f0' width='${width}' height='${height}'/%3E%3C/svg%3E`;
}

/**
 * Optimized Image Component
 */
const OptimizedImage = memo(function OptimizedImage({
  src: rawSrc,
  alt = '',
  width,
  height,
  className = '',
  fallbackSrc = '/assets/placeholder-image.png',
  aspectRatio,
  sizes = '100vw',
  priority = false,
  onLoad,
  onError,
  objectFit = 'cover',
  quality = 80,
  ...props
}) {
  // Defensive: legacy records may store absolute URLs baked against a
  // dev host (e.g. http://localhost:3000/assets/foo.jpg). Strip those
  // back to host-relative paths so the image loads against whichever
  // domain the page is currently being served from. No-op for already-
  // portable values (bare paths and Firebase Storage URLs).
  const src = rawSrc ? (resolveImageSrc(rawSrc) || rawSrc) : rawSrc;
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [inView, setInView] = useState(priority);
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  // Reserve aspect-ratio space using the modern CSS `aspect-ratio`
  // property (replaces the legacy `padding-top: X%; height: 0` trick which
  // forced downstream consumers to use `!important` overrides to fill the
  // container). `hasAspect` controls absolute positioning of the <img>
  // child so it stretches to the reserved box.
  const aspectRatioCss = aspectRatio
    ? aspectRatio
    : height && width
      ? `${width} / ${height}`
      : undefined;
  const hasAspect = !!aspectRatioCss;

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  // Handle image load
  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  // Track whether we've already attempted the full-resolution fallback so
  // we don't loop between variant failure and full-image failure.
  const [triedFullFallback, setTriedFullFallback] = useState(false);

  // Handle image error. The most common failure mode is a derived variant
  // (med_800_*.webp) being unavailable or its download token going stale —
  // in that case the *original* URL is still valid, so try it once before
  // surrendering to the static placeholder.
  const handleError = () => {
    if (!triedFullFallback && variants?.full && variants.full !== mainSrc) {
      setTriedFullFallback(true);
      return;
    }
    setHasError(true);
    onError?.();
  };

  // Derive variant URLs (thumb 320, med 800, full original) + srcSet.
  // Falls back to { full: src } for external URLs / pre-backfill images,
  // in which case the browser just downloads the original.
  const variants = src ? getImageVariants(src) : { full: src };
  const thumbSrc = variants.thumb;
  // Pick a sensible "main" src: medium (when available) keeps payload
  // small on cards; full is preserved in srcSet for retina/desktop. After
  // a single failed load we fall back to the full original (skipping the
  // medium variant entirely) before surrendering to the placeholder.
  const preferredSrc = variants.med || variants.full || src;
  const mainSrc = triedFullFallback ? (variants.full || src) : preferredSrc;

  // Generate srcset using the derived variants. Returns undefined when no
  // variants are available so the browser falls back to plain `src`. Once
  // we've fallen back to the original we skip srcSet too — otherwise the
  // browser may re-pick the broken variant.
  const generateSrcSet = () => (triedFullFallback ? undefined : (variants.srcSet || undefined));

  const imageSrc = hasError ? fallbackSrc : mainSrc;
  // Placeholder SVG can be used for future blur-up effect
  // const placeholder = generatePlaceholder(width || 100, height || 100);

  return (
    <div 
      ref={containerRef}
      className={`optimized-image ${className} ${isLoaded ? 'loaded' : ''} ${hasError ? 'error' : ''}`}
      style={{
        position: 'relative',
        overflow: 'hidden',
        ...(hasAspect && { aspectRatio: aspectRatioCss })
      }}
      {...props}
    >
      {/* Placeholder — blurred thumbnail when available (LQIP), otherwise
          a solid parchment swatch. Hidden once the main image has loaded. */}
      {!isLoaded && (
        thumbSrc && !hasError ? (
          <img
            src={thumbSrc}
            alt=""
            aria-hidden="true"
            className="image-placeholder image-placeholder--lqip"
            decoding="async"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit,
              filter: 'blur(12px)',
              transform: 'scale(1.05)', // hide blurred edges
              backgroundColor: SURFACE.parchmentSoft,
            }}
          />
        ) : (
          <div 
            className="image-placeholder"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: SURFACE.parchmentSoft
            }}
          />
        )
      )}

      {/* Main Image */}
      {inView && (
        <img
          ref={imgRef}
          src={imageSrc}
          alt={alt}
          width={width}
          height={height}
          sizes={sizes}
          srcSet={generateSrcSet()}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
          style={{
            position: hasAspect ? 'absolute' : 'relative',
            top: 0,
            left: 0,
            width: '100%',
            height: hasAspect ? '100%' : 'auto',
            objectFit,
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease'
          }}
        />
      )}

      {/* Loading indicator */}
      {!isLoaded && inView && (
        <div className="image-loading">
          <div className="loading-spinner" />
        </div>
      )}
    </div>
  );
});

/**
 * Image with blur-up effect
 */
export const BlurUpImage = memo(function BlurUpImage({
  src,
  lowResSrc,
  alt = '',
  className = '',
  ...props
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div 
      ref={containerRef} 
      className={`blur-up-image ${className} ${isLoaded ? 'loaded' : ''}`}
      {...props}
    >
      {/* Low res placeholder */}
      {lowResSrc && (
        <img
          src={lowResSrc}
          alt=""
          className="blur-placeholder"
          aria-hidden="true"
        />
      )}

      {/* Full res image */}
      {inView && (
        <img
          src={src}
          alt={alt}
          className="full-image"
          onLoad={() => setIsLoaded(true)}
        />
      )}
    </div>
  );
});

/**
 * Avatar Image with initials fallback
 */
export const AvatarImage = memo(function AvatarImage({
  src,
  alt = '',
  name = '',
  size = 40,
  className = ''
}) {
  const [hasError, setHasError] = useState(false);

  // Generate initials from name
  const initials = name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  // Generate color from name — uses brand AVATAR_PALETTE so initials
  // avatars feel like the rest of the UI rather than a Material rainbow.
  const getColorFromName = (name) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
  };

  if (!src || hasError) {
    return (
      <div
        className={`avatar-initials ${className}`}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: getColorFromName(name),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: size * 0.4,
          fontWeight: 600
        }}
      >
        {initials || '?'}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={`avatar-image ${className}`}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        objectFit: 'cover'
      }}
      onError={() => setHasError(true)}
    />
  );
});

/**
 * Image Gallery with lazy loading
 */
export const LazyImageGallery = memo(function LazyImageGallery({
  images = [],
  columns = 3,
  gap = 8,
  aspectRatio = 1,
  onImageClick
}) {
  return (
    <div 
      className="lazy-image-gallery"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap
      }}
    >
      {images.map((image, index) => (
        <div
          key={image.id || index}
          className="gallery-item"
          onClick={() => onImageClick?.(image, index)}
        >
          <OptimizedImage
            src={image.src || image.url}
            alt={image.alt || `Image ${index + 1}`}
            aspectRatio={aspectRatio}
            objectFit="cover"
          />
        </div>
      ))}
    </div>
  );
});

/**
 * Background Image with lazy loading
 */
export const LazyBackgroundImage = memo(function LazyBackgroundImage({
  src,
  children,
  className = '',
  style = {},
  ...props
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          // Preload image
          const img = new Image();
          img.onload = () => setIsLoaded(true);
          img.src = src;
          observer.disconnect();
        }
      },
      { rootMargin: '100px' }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [src]);

  return (
    <div
      ref={containerRef}
      className={`lazy-background ${className} ${isLoaded ? 'loaded' : ''}`}
      style={{
        ...style,
        backgroundImage: inView && isLoaded ? `url(${src})` : 'none',
        backgroundColor: SURFACE.parchmentSoft,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        transition: 'background-image 0.3s ease'
      }}
      {...props}
    >
      {children}
    </div>
  );
});

/**
 * SmartImage — preferred name in new code. Same component as OptimizedImage,
 * renamed to signal that it derives thumbnail + medium variants from the
 * source URL automatically (see src/utils/imageVariants.js).
 */
export const SmartImage = OptimizedImage;

export default OptimizedImage;
